import { FLICKER_KINDS, MAX_LIGHTS_PER_VIEW, frameId, lightOn, lightPoint, lightSeed, type AssetManifest, type CompiledChunk } from "daedalus-core";
import { LIGHT_STRIDE, type LightingFrame } from "daedalus-renderer-phaser";

/**
 * The lights of the booted map (ADR-141's amendment of 2026-11-01, decisions 4 and 5): every placed prop whose frame has a
 * light in the asset manifest, listed once when the map boots into flat typed arrays, and each frame the nearest
 * `MAX_LIGHTS_PER_VIEW` that reach the view written into the light layer's frame. A dusk-to-dawn light that is off shows its
 * frame's unlit art. Nothing here allocates per frame.
 */

/**
 * How far from a light's anchor its prop's art may reach, in world pixels: a dusk copy this near the view is shown its unlit art
 * each frame while off, so a copy that enters the view by day never shows its lit art.
 */
const ART_REACH = 512;

/**
 * A map's lights in flat arrays, one slot per placed copy. `ids` are the props' ids, `lit` the frame each was placed with
 * and `unlit` the frame it shows while off (null when the art does not change).
 */
export interface MapLights {
  readonly count: number;
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly radius: Float32Array;
  readonly rgb: Uint8Array;
  readonly strength: Float32Array;
  readonly amount: Float32Array;
  readonly hz: Float32Array;
  readonly wild: Float32Array;
  readonly wobble: Float32Array;
  readonly seed: Float32Array;
  readonly dusk: Uint8Array;
  readonly ids: readonly string[];
  readonly lit: readonly string[];
  readonly unlit: readonly (string | null)[];
  /** Scratch for the nearest-first pick: the chosen slots and their squared distances. */
  readonly chosen: Int32Array;
  readonly distances: Float64Array;
  /** Whether each copy with unlit art shows it now, so the art changes only when the light turns on or off. */
  readonly showsUnlit: Uint8Array;
  /** How many copies show their unlit art now, in its one slot, for the page's report. */
  readonly unlitShown: Int32Array;
  /** Whether each copy is an energy source the server put out (ADR-141's amendment of 2026-11-01, decision 7): it gives no light and shows its unlit art. */
  readonly out: Uint8Array;
  /** The slots of each placed prop's id. */
  readonly slotsOf: ReadonlyMap<string, readonly number[]>;
}

/**
 * Lists the lights of a map's client chunks.
 *
 * @param chunks The map's client chunks.
 * @param assets The asset manifest, whose `lights` and `frames` are read.
 * @param tileSize Tile size in pixels.
 * @returns The lights; empty for a manifest with none.
 */
export function readMapLights(chunks: Iterable<CompiledChunk>, assets: Pick<AssetManifest, "frames" | "lights">, tileSize: number): MapLights {
  const found: { id: string; lit: string; unlit: string | null; x: number; y: number; radius: number; r: number; g: number; b: number; strength: number; amount: number; hz: number; wild: number; wobble: number; dusk: boolean }[] = [];
  const lights = assets.lights ?? {};
  const point = { x: 0, y: 0 };
  if (Object.keys(lights).length > 0) {
    for (const chunk of chunks) {
      for (const layer of chunk.layers) {
        if (layer.kind !== "prop") continue;
        for (const prop of layer.props) {
          const id = frameId(prop.frame.tileset, null, prop.frame.tile);
          const light = lights[id];
          const rect = assets.frames[id];
          if (light === undefined || rect === undefined) continue;
          lightPoint(light, prop, rect, tileSize, point);
          const flicker = FLICKER_KINDS[light.flicker];
          found.push({ id: prop.id, lit: id, unlit: light.unlit ?? null, x: point.x, y: point.y, radius: light.radius, r: light.rgb[0], g: light.rgb[1], b: light.rgb[2], strength: light.strength, amount: flicker.amount, hz: flicker.hz, wild: flicker.wild, wobble: flicker.wobble, dusk: light.when === "dusk" });
        }
      }
    }
  }
  const count = found.length;
  const out: MapLights = {
    count,
    x: new Float32Array(count),
    y: new Float32Array(count),
    radius: new Float32Array(count),
    rgb: new Uint8Array(count * 3),
    strength: new Float32Array(count),
    amount: new Float32Array(count),
    hz: new Float32Array(count),
    wild: new Float32Array(count),
    wobble: new Float32Array(count),
    seed: new Float32Array(count),
    dusk: new Uint8Array(count),
    ids: found.map((entry) => entry.id),
    lit: found.map((entry) => entry.lit),
    unlit: found.map((entry) => entry.unlit),
    chosen: new Int32Array(MAX_LIGHTS_PER_VIEW),
    distances: new Float64Array(MAX_LIGHTS_PER_VIEW),
    showsUnlit: new Uint8Array(count),
    unlitShown: new Int32Array(1),
    out: new Uint8Array(count),
    slotsOf: slotsByProp(found.map((entry) => entry.id)),
  };
  found.forEach((entry, index) => {
    out.x[index] = entry.x;
    out.y[index] = entry.y;
    out.radius[index] = entry.radius;
    out.rgb[index * 3] = entry.r;
    out.rgb[index * 3 + 1] = entry.g;
    out.rgb[index * 3 + 2] = entry.b;
    out.strength[index] = entry.strength;
    out.amount[index] = entry.amount;
    out.hz[index] = entry.hz;
    out.wild[index] = entry.wild;
    out.wobble[index] = entry.wobble;
    out.seed[index] = lightSeed(entry.id);
    out.dusk[index] = entry.dusk ? 1 : 0;
  });
  return out;
}

/**
 * The slots each prop id holds.
 *
 * @param ids The ids in slot order.
 * @returns The slots by id.
 */
function slotsByProp(ids: readonly string[]): ReadonlyMap<string, readonly number[]> {
  const slots = new Map<string, number[]>();
  ids.forEach((id, slot) => {
    const held = slots.get(id);
    if (held === undefined) slots.set(id, [slot]);
    else held.push(slot);
  });
  return slots;
}

/**
 * The placed prop's id inside a `source` key (`fire:camp-1` names `camp-1`).
 *
 * @param key The source's family-qualified key.
 * @returns The prop's id.
 */
export function propOfSourceKey(key: string): string {
  return key.slice(key.indexOf(":") + 1);
}

/**
 * Sets whether a source is out on the lights of a map: a source that is out gives no light and shows its unlit art, one that
 * is lit again takes its compiled state back. A key the map has no light for changes nothing.
 *
 * @param lights The map's lights.
 * @param key The source's family-qualified key.
 * @param lit False when the server put the source out, true when it relit it.
 */
export function setSourceLit(lights: MapLights, key: string, lit: boolean): void {
  for (const slot of lights.slotsOf.get(propOfSourceKey(key)) ?? []) {
    lights.out[slot] = lit ? 0 : 1;
  }
}

/**
 * The sky a frame's lights are placed under.
 */
export interface LightSky {
  readonly ambient: number;
  readonly nightLevel: number;
  readonly indoor: boolean;
}

/**
 * Writes the nearest `MAX_LIGHTS_PER_VIEW` lights that reach the view into the frame, each with its share for the sky and
 * its flicker, and turns each dusk-to-dawn copy's art to unlit or lit as its light goes off or on.
 *
 * @param lights The map's lights.
 * @param frame The frame, whose view rectangle is set.
 * @param sky The sky.
 * @param show Shows a frame on a placed prop, by its id (a no-op when it shows already): called when a copy's light turns on or
 *   off, and each frame for an unlit copy near the view.
 * @returns The lights written.
 */
export function placeMapLights(lights: MapLights, frame: LightingFrame, sky: LightSky, show: (id: string, frame: string) => void): number {
  const centreX = frame.left + frame.width / 2;
  const centreY = frame.top + frame.height / 2;
  const right = frame.left + frame.width;
  const bottom = frame.top + frame.height;
  const always = lightOn(sky.ambient, sky.nightLevel, "always", sky.indoor);
  const dusk = lightOn(sky.ambient, sky.nightLevel, "dusk", sky.indoor);
  const offAtDusk = dusk === 0;
  let chosen = 0;
  for (let index = 0; index < lights.count; index += 1) {
    const isDusk = lights.dusk[index] === 1;
    const isOut = lights.out[index] === 1;
    const x = lights.x[index] as number;
    const y = lights.y[index] as number;
    const radius = lights.radius[index] as number;
    const unlit = lights.unlit[index] as string | null;
    if ((isDusk || isOut || lights.showsUnlit[index] === 1) && unlit !== null) {
      const near = x + ART_REACH >= frame.left && x - ART_REACH <= right && y + ART_REACH >= frame.top && y - ART_REACH <= bottom;
      const wanted = isOut || (isDusk && offAtDusk) ? 1 : 0;
      if (lights.showsUnlit[index] !== wanted || (wanted === 1 && near)) {
        lights.unlitShown[0] = (lights.unlitShown[0] as number) + wanted - (lights.showsUnlit[index] as number);
        lights.showsUnlit[index] = wanted;
        show(lights.ids[index] as string, wanted === 1 ? unlit : (lights.lit[index] as string));
      }
    }
    if (isOut || (isDusk && offAtDusk)) continue;
    if (x + radius < frame.left || x - radius > right || y + radius < frame.top || y - radius > bottom) continue;
    const distance = (x - centreX) * (x - centreX) + (y - centreY) * (y - centreY);
    if (chosen === MAX_LIGHTS_PER_VIEW && distance >= (lights.distances[chosen - 1] as number)) continue;
    let at = chosen === MAX_LIGHTS_PER_VIEW ? chosen - 1 : chosen;
    while (at > 0 && (lights.distances[at - 1] as number) > distance) {
      lights.distances[at] = lights.distances[at - 1] as number;
      lights.chosen[at] = lights.chosen[at - 1] as number;
      at -= 1;
    }
    lights.distances[at] = distance;
    lights.chosen[at] = index;
    if (chosen < MAX_LIGHTS_PER_VIEW) chosen += 1;
  }
  const out = frame.lights;
  for (let slot = 0; slot < chosen; slot += 1) {
    const index = lights.chosen[slot] as number;
    const at = slot * LIGHT_STRIDE;
    out[at] = lights.x[index] as number;
    out[at + 1] = lights.y[index] as number;
    out[at + 2] = lights.radius[index] as number;
    out[at + 3] = lights.rgb[index * 3] as number;
    out[at + 4] = lights.rgb[index * 3 + 1] as number;
    out[at + 5] = lights.rgb[index * 3 + 2] as number;
    out[at + 6] = (lights.strength[index] as number) * (lights.dusk[index] === 1 ? dusk : always);
    out[at + 7] = lights.amount[index] as number;
    out[at + 8] = lights.hz[index] as number;
    out[at + 9] = lights.seed[index] as number;
    out[at + 10] = lights.wild[index] as number;
    out[at + 11] = lights.wobble[index] as number;
  }
  return chosen;
}

/**
 * The page's holder of the booted map's lights, which the scene fills when a map boots and empties when it leaves, and the
 * light module reads each frame.
 */
export interface MapLightHolder {
  lights: MapLights | null;
  /** The keys of the sources the server has told the page are out, kept until the stage ends, so a `source` that arrives before the map's lights are listed is applied when they are. */
  readonly out: Set<string>;
  show: (id: string, frame: string) => void;
}

/**
 * The page's one holder.
 */
export const PAGE_MAP_LIGHTS: MapLightHolder = { lights: null, out: new Set(), show: () => undefined };

/**
 * Applies a `source` message to the page's holder: records it and, when the map's lights are listed, sets the copies.
 *
 * @param holder The holder.
 * @param key The source's family-qualified key.
 * @param lit False when the source went out, true when it was relit.
 */
export function hearSource(holder: MapLightHolder, key: string, lit: boolean): void {
  if (lit) holder.out.delete(key);
  else holder.out.add(key);
  if (holder.lights !== null) setSourceLit(holder.lights, key, lit);
}

/**
 * Forgets every source the page was told is out and lights the map's copies again: a welcome starts the server's news afresh
 * (a restart or a swap holds every source lit), and the server tells a page again what is out in its view.
 *
 * @param holder The holder.
 */
export function forgetSources(holder: MapLightHolder): void {
  holder.out.clear();
  holder.lights?.out.fill(0);
}

/**
 * Sets every source the holder knows is out on a map's freshly listed lights.
 *
 * @param holder The holder.
 * @param lights The map's lights.
 */
export function applyKnownSources(holder: MapLightHolder, lights: MapLights): void {
  for (const key of holder.out) setSourceLit(lights, key, false);
}
